/*
 * Shop Pay later (2026-10-28).
 * <os-deferred-payment-button> sits where `form | payment_button` used to print Shopify's accelerated checkout block
 * (snippets/buy-buttons.liquid). It holds a skeleton the same size as Shopify's, so nothing moves when the real
 * block arrives. The real block is fetched from sections/os-payment-button.liquid (Section Rendering API) for the
 * variant currently in the form, put in place of the skeleton, and Shopify.PaymentButton.init() is called, which is
 * Shopify's own loader (the same call Prestige makes after a variant change or a quick-buy open).
 *
 * When: after the window `load` event (photos and text are painted) once the browser is idle (max 2 s wait), or
 * earlier if the shopper scrolls to within one screen of the buy box or touches/focuses the product form.
 * After the first start, any new instance (variant re-render, quick-buy modal) loads straight away.
 */
const SECTION = 'os-payment-button';
const waiting = new Set();
let started = false;
const cache = new Map();

function start() {
  if (started) return;
  started = true;
  removeEventListener('scroll', onScroll);
  waiting.forEach((el) => el.load());
  waiting.clear();
}

function onScroll() {
  for (const el of waiting) {
    if (el.getBoundingClientRect().top < innerHeight * 2) return start();
  }
}

function afterLoad() {
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
  idle(start, { timeout: 2000 });
}

if (document.readyState === 'complete') afterLoad();
else addEventListener('load', afterLoad, { once: true });
addEventListener('scroll', onScroll, { passive: true });

class OsDeferredPaymentButton extends HTMLElement {
  connectedCallback() {
    if (started) return this.load();
    waiting.add(this);
    const form = this.form();
    if (form && !form.dataset.osPbListen) {
      form.dataset.osPbListen = '1';
      ['pointerdown', 'focusin'].forEach((t) => form.addEventListener(t, start, { once: true, passive: true }));
    }
  }

  disconnectedCallback() {
    waiting.delete(this);
  }

  form() {
    return this.closest('form') || document.getElementById(this.getAttribute('form-id'));
  }

  async load() {
    if (this.dataset.state) return;
    this.dataset.state = 'loading';
    const form = this.form();
    const variant = (form && form.elements.id && form.elements.id.value) || this.getAttribute('variant');
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const url = `${root}products/${this.getAttribute('handle')}?variant=${encodeURIComponent(variant)}&section_id=${SECTION}`;
    try {
      if (!cache.has(url)) cache.set(url, fetch(url, { credentials: 'same-origin' }).then((r) => (r.ok ? r.text() : Promise.reject(r.status))));
      const html = await cache.get(url);
      if (!this.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const block = doc.querySelector('.shopify-payment-button');
      if (!block) throw new Error('no payment button in section');
      this.replaceChildren(document.importNode(block, true));
      // Extras payment_button adds to its form: the buyer-consent notice and the product-id input.
      const consent = doc.getElementById('shopify-buyer-consent');
      if (consent && !document.getElementById('shopify-buyer-consent')) this.after(document.importNode(consent, true));
      const pid = doc.querySelector('input[name="product-id"]');
      if (form && pid && !form.querySelector('input[name="product-id"]')) form.appendChild(document.importNode(pid, true));
      this.dataset.state = 'ready';
      if (window.Shopify && Shopify.PaymentButton && Shopify.PaymentButton.init) Shopify.PaymentButton.init();
    } catch (e) {
      cache.delete(url);
      this.dataset.state = 'failed';
      this.hidden = true; // Add to basket stays; the shopper loses only the express button.
    }
  }
}

if (!customElements.get('os-deferred-payment-button')) customElements.define('os-deferred-payment-button', OsDeferredPaymentButton);
